import PubSub from 'pubsub-js';
import { Box3, Object3D, Vector2, Vector3 } from 'three';

import { DynamicAssetType } from '../../../../typings/api/skymap/rest/v1/.common';
import { WebsocketMessage } from '../../../../typings/websocket-messages';
import { SectionExpandState } from '../../react/components/skyview/right-menu/tools/design-tool-menu/design-tool-menu';
import { ToastrMessage } from '../../react/state/toaster-types';
import { SkyviewCamera } from '../cameras/types';
import { PointId } from '../elements/design/design-node/utils/point-node';
import { AssetCategory, ElementObj } from '../elements/element-obj';
import { Geometry } from '../elements/geometry';
import { VolumeModelState } from '../elements/volume/types';
import { VolumeHandler } from '../elements/volume/volume-handler';
import { SceneType } from '../scene-manager';
import { Tool } from '../tools/tool';
import { Coordinates, Point2d } from '../utilities/math-helpers';
import { WmsToolLayerItem } from '../wms-state';

export enum SubscriptionTopic {
  OnInspectionToolImageHover,
  OpenImageInspectionDialog,
  ShowSetAssetCoordinateDialog,
  ShowSaveGeometryAssetDialog,
  ShowCreateDesignTextDialog,
  OnReferencePointTransformCoordinateASet,
  OnReferencePointTransformCoordinateBSet,
  SkyviewAssetCategoryVisibilityChanged,
  SkyviewAssetFocusChanged,
  SkyviewAssetVisibilityChanged,
  WmsAttributionsChanged,
  ToastrMessage,
  DesignToolMultiselectUpdated,
  DesignToolActiveFeatureChanged,
  DesignToolMenuStateChanged,
  DesignToolActiveActionChanged,
  DesignAttributesChanged,
  DesignDoubleSideChanged,
  RefreshAssetList,
  ModelVolumeStateChanged,
  ShowAssetMenuCheckBoxes,
  HideAssetMenuCheckBoxes,
  ShowOnlySpecificAssets,
  ShowAssetMenuOverlay,
  ShowDesignLicenseDialog,
  HideAssetMenuOverlay,
  SaveAssetMenuVisibilitySnapshot,
  RestoreAssetMenuVisibilitySnapshot,
  AssetMenuCategoryCheckBoxesChanged,
  OnSceneChangeStarted,
  OnSceneChangeCompleted,
  OnSkyViewWmsMapToggled,
  OnSkyViewWmsLayerChanged,
  OnCameraMoved,
  OnCameraChanged,
  OnSceneBoundingBoxChanged,
  ViewerCursorPositionChanged,
  ErrorFetchingProjectInfo,
  ShowDeleteAssetDialog,
  WebsocketStatusChanged,
  WebsocketMessageRecieved,
  OnWebsocketReconnected,
  AssetHighlightedIndexChanged,
  AssetSelectedInViewer,
  OpenPoiPreviewDialog,
  ProfileSectionOffsetUpdated,
  ProfileSectionUpdated,
  /** The layer that is inspected in a glb asset changed, see Glb.inspectLayer. */
  LayerInspected,
  /** A layer of a glb asset was shown or hidden outside the layer list, see Glb.hideLayer. */
  LayerVisibilityChanged,
  /** The layer that is hovered in a glb asset changed, see Glb.hoverLayer. */
  LayerHovered,
  OnProjectionUpdated = 'onProjectionUpdated',
  GcpXmlChanged = 'gcpXmlChanged',
  SkyviewLoadingProgress = 'skyviewLoadingProgress',
  SkyviewLoadingProgressError = 'skyviewLoadingProgressError',
  AssetListChanged = 'assetListChanged',
  AssetChanged = 'assetChanged',
  SetTool = 'setTool',
  UnSelection = 'unSelection',
  NewSelection = 'newSelection',
  RightMenuToggled = 'rightMenuToggled',
  VolumeInspectorToggleModal = 'volumeInspectorToggleModal',
  I18NextLanguageChanged = 'I18NextLanguageChanged',
  SignOut = 'signout',
}

type OnReferencePointTransformCoordinateASet = Coordinates;
type WebsocketMessageRecieved = WebsocketMessage;
type WebsocketStatusChanged = { status: 'connected' | 'connecting' | 'disconnected' };

type WmsAttributionsChanged = string[];

type OnProjectionUpdated = {
  old: { projection: string; origo?: Vector3 };
  new: { projection: string; origo?: Vector3 };
};

type OpenImageInspectionDialog = {
  url: string;
  name: string;
};

type OnInspectionToolImageHover = {
  data?: {
    url: string;
    name: string;
    position: Point2d;
  };
};

type OnReferencePointTransformCoordinateBSet = Coordinates;

type ShowDeleteAssetDialog = {
  asset: ElementObj;
  position?: Point2d;
};

type ShowCreateDesignTextDialog = {
  origin: Vector3;
  rotation: number;
  text: string;
  size: number;
  startPointId?: PointId;
};

type ShowSaveGeometryAssetDialog = {
  asset: Geometry;
  client?: Vector2;
};

type OnSceneBoundingBoxChanged = {
  boundingBox: Box3;
};

type ShowSetAssetCoordinateDialog = {
  originWorldPosition?: Vector3;
  asset: ElementObj;
  infoMessage?: string;
  title?: string;
};

type SkyviewAssetCategoryVisibilityChanged = {
  categoryType: AssetCategory;
  enabled: boolean;
};

type SkyviewAssetVisibilityChanged = {
  assetUuid: string;
  visible: boolean;
};

type SkyviewAssetFocusChanged = {
  /**
   * Id of asset focused or undefined if no asset is currently focused.
   */
  assetUuid: string | undefined;
};

type SkyviewLoadingProgress = {
  progress: number;
  message?: string;
};

type SkyviewLoadingProgressError = Record<string, never>;

type AssetListChanged = {
  assets?: Object3D[];
};

type VolumeInspectorToggleModal = {
  display: boolean;
  volumeHandler: VolumeHandler;
};

type RefreshAssetList = {
  projectId: string;
  assetTypes: DynamicAssetType[];
};

type VolumeModelStateChanged = {
  volumeId: string;
  state: VolumeModelState;
};

type ShowAssetMenuCheckBoxes = {
  categories: AssetCategory[];
  autoCheckVisibleAssets?: boolean;
  autoCheckAssetIds?: string[];
  disableAll?: boolean;
};

type ShowOnlySpecificAssets = {
  assetUuids: string[];
};

type AssetMenuCategoryCheckBoxesChanged = {
  category: AssetCategory;
  checkedAssetUuids: string[];
};

type OnSceneChange = {
  scene: SceneType;
};

type OnCameraMoved = {
  camera: SkyviewCamera;
};

type OnWmsLayerChanged = {
  layerItem: WmsToolLayerItem;
};

// Map subscription topic to message type.
// prettier-ignore
export type PubSubMessageType<T> = T extends SubscriptionTopic.SkyviewAssetCategoryVisibilityChanged
  ? SkyviewAssetCategoryVisibilityChanged
  : T extends SubscriptionTopic.SkyviewAssetVisibilityChanged
  ? SkyviewAssetVisibilityChanged
  : T extends SubscriptionTopic.OnReferencePointTransformCoordinateASet
  ? OnReferencePointTransformCoordinateASet
  : T extends SubscriptionTopic.OnReferencePointTransformCoordinateBSet
  ? OnReferencePointTransformCoordinateBSet
  : T extends SubscriptionTopic.SkyviewAssetFocusChanged
  ? SkyviewAssetFocusChanged
  : T extends SubscriptionTopic.ToastrMessage
  ? ToastrMessage
  : T extends SubscriptionTopic.WmsAttributionsChanged
  ? WmsAttributionsChanged
  : T extends SubscriptionTopic.SkyviewLoadingProgress
  ? SkyviewLoadingProgress
  : T extends SubscriptionTopic.SkyviewLoadingProgressError
  ? SkyviewLoadingProgressError
  : T extends SubscriptionTopic.AssetChanged
  ? ElementObj
  : T extends SubscriptionTopic.OnProjectionUpdated
  ? OnProjectionUpdated
  : T extends SubscriptionTopic.ShowSetAssetCoordinateDialog
  ? ShowSetAssetCoordinateDialog
  : T extends SubscriptionTopic.ShowSaveGeometryAssetDialog
  ? ShowSaveGeometryAssetDialog
  : T extends SubscriptionTopic.AssetListChanged
  ? AssetListChanged
  : T extends SubscriptionTopic.SetTool
  ? Tool
  : T extends SubscriptionTopic.NewSelection
  ? ElementObj
  : T extends SubscriptionTopic.AssetSelectedInViewer
  ? ElementObj
  : T extends SubscriptionTopic.OpenPoiPreviewDialog
  ? ElementObj
  : T extends SubscriptionTopic.RightMenuToggled
  ? boolean
  : T extends SubscriptionTopic.DesignToolMenuStateChanged
  ? Partial<SectionExpandState>
  : T extends SubscriptionTopic.VolumeInspectorToggleModal
  ? VolumeInspectorToggleModal
  : T extends SubscriptionTopic.ShowDeleteAssetDialog
  ? ShowDeleteAssetDialog
  : T extends SubscriptionTopic.ShowCreateDesignTextDialog
  ? ShowCreateDesignTextDialog
  : T extends SubscriptionTopic.RefreshAssetList
  ? RefreshAssetList
  : T extends SubscriptionTopic.ModelVolumeStateChanged
  ? VolumeModelStateChanged
  : T extends SubscriptionTopic.ShowAssetMenuCheckBoxes
  ? ShowAssetMenuCheckBoxes
  : T extends SubscriptionTopic.HideAssetMenuCheckBoxes
  ? undefined
  : T extends SubscriptionTopic.ShowOnlySpecificAssets
  ? ShowOnlySpecificAssets
  : T extends SubscriptionTopic.ShowAssetMenuOverlay
  ? undefined
  : T extends SubscriptionTopic.ShowDesignLicenseDialog
  ? undefined
  : T extends SubscriptionTopic.HideAssetMenuOverlay
  ? undefined
  : T extends SubscriptionTopic.SaveAssetMenuVisibilitySnapshot
  ? undefined
  : T extends SubscriptionTopic.ProfileSectionUpdated
  ? undefined
  : T extends SubscriptionTopic.ProfileSectionOffsetUpdated
  ? number
  : T extends SubscriptionTopic.AssetMenuCategoryCheckBoxesChanged
  ? AssetMenuCategoryCheckBoxesChanged
  : T extends SubscriptionTopic.OnSceneBoundingBoxChanged
  ? OnSceneBoundingBoxChanged
  : T extends SubscriptionTopic.ViewerCursorPositionChanged
  ? Coordinates | null
  : T extends SubscriptionTopic.OnSceneChangeStarted
  ? OnSceneChange
  : T extends SubscriptionTopic.OnSceneChangeCompleted
  ? OnSceneChange
  : T extends SubscriptionTopic.OnCameraChanged
  ? OnCameraMoved
  : T extends SubscriptionTopic.OnCameraMoved
  ? OnCameraMoved
  : T extends SubscriptionTopic.OnSkyViewWmsLayerChanged
  ? OnWmsLayerChanged
  : T extends SubscriptionTopic.WebsocketMessageRecieved
  ? WebsocketMessageRecieved
  : T extends SubscriptionTopic.WebsocketStatusChanged
  ? WebsocketStatusChanged
  : T extends SubscriptionTopic.OnWebsocketReconnected
  ? undefined
  : T extends SubscriptionTopic.OpenImageInspectionDialog
  ? OpenImageInspectionDialog
  : T extends SubscriptionTopic.OnInspectionToolImageHover 
  ? OnInspectionToolImageHover 
  : T extends SubscriptionTopic.AssetHighlightedIndexChanged
  ? undefined
  : T extends SubscriptionTopic.LayerInspected
  ? ElementObj
  : T extends SubscriptionTopic.LayerVisibilityChanged
  ? ElementObj
  : T extends SubscriptionTopic.LayerHovered
  ? ElementObj
  : undefined;

export function publish<T extends SubscriptionTopic>(topic: T, message?: PubSubMessageType<T>) {
  PubSub.publish(topic, message);
}

export function subscribe<T extends SubscriptionTopic>(
  topic: T,
  func: (message: PubSubMessageType<T>) => void,
): string {
  return PubSub.subscribe(topic, (topic: T, message: PubSubMessageType<T>) => {
    // Don't pass topic as we only subscribe on one topic at a time.
    func(message);
  });
}

export function unsubscribe(subscriptionId: string) {
  PubSub.unsubscribe(subscriptionId);
}
